// Step 1b — Email verification (Figma 5/6/7).
//
// THE ACCOUNT IS CREATED HERE. A correct code returns clientId + JWT and the
// server-applied defaults (city from the geo guess, self-represented,
// subscribed to job notifications). Success shows "Code verified!" and
// auto-advances after 3s.
//
// The wait (decided 2026-10-28): the boxes turn green with "Checking…" as the
// 6th digit lands; an answer still out after CHECKLIST_AFTER_MS swaps them for
// the account checklist (AccountSetupProgress), which ticks its last steps when
// the answer arrives and holds them a moment before "Code verified!". A wrong
// code is answered before then, so it shows under the boxes as always.

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionCodeInput, { shouldClearCode } from "../common/SubmissionCodeInput";
import ResendCountdown from "../common/ResendCountdown";
import { isMockApi } from "../../../shared/api/submission";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { trackSubmissionEvent } from "../analytics";
import { membershipPrimary } from "../common/membershipButton";
import SuccessCheckIcon from "../../../images/success-check.svg";
import { autoContinueLabel, useAutoContinue } from "../common/autoContinue";
import AccountSetupProgress from "../common/AccountSetupProgress";
import { preloadCityOptions } from "../common/cityOptions";

const CODE_LENGTH = 6;
/** A right code makes the account in the same request; past this, the checklist shows. */
export const CHECKLIST_AFTER_MS = 1500;
/** The ticked checklist stays this long before "Code verified!". */
const CHECKLIST_HOLD_MS = 700;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const SignupVerifyEmailPage: React.FC<Props> = ({
  history,
  location,
  sessionId,
  maskedEmail,
  attemptsRemaining,
  verifyStatus,
  resendAvailableAt,
  errorCode,
  errorMessage,
  accountDefaults,
  verifyEmailCode,
  resendEmailCode,
  loadSignupState,
  refreshGate,
  clearError,
}) => {
  const [code, setCode] = useState("");
  const [restoring, setRestoring] = useState(!sessionId);
  const [restoreFailed, setRestoreFailed] = useState(false);
  // The answer is slow: the checklist instead of the boxes (until the answer, and a moment after).
  const [slow, setSlow] = useState(false);
  const slowRef = useRef(false);
  const submittedAt = useRef(0);
  const showSlow = (value: boolean) => {
    slowRef.current = value;
    setSlow(value);
  };
  const autoContinue = useAutoContinue(() => goNext());
  // Mirrors the latest errorCode so post-await code reads the current value
  // instead of the one captured when the handler was created.
  const errorCodeRef = useRef<string | null>(errorCode);
  errorCodeRef.current = errorCode;
  const getVerifyErrorCode = () => errorCodeRef.current;

  const boot = async () => {
    // A user already emailed a code must be able to finish even with no flow
    // record, or the code is wasted; otherwise `bd` is required.
    if (!hasFlowContext({ search: location.search, hasSignupSession: !!sessionId })) {
      history.replace(fallbackTo(location.search));
      return;
    }
    // Already verified in another tab (or a back-navigation): don't offer a
    // second account creation — move forward.
    if (isSubmissionAuthed()) {
      history.replace(`/submission/flow/step/2${location.search}`);
      return;
    }
    if (!sessionId) {
      const restored = await loadSignupState();
      if (!restored.ok) {
        // Only a genuinely missing session means "start again"; a network
        // blip or 5xx gets a retry so a valid code isn't thrown away.
        // No session to verify in this browser: the continue page offers log
        // in (account holders) or a fresh start with the breakdown kept.
        if (restored.reason === "transient") setRestoreFailed(true);
        else {
          history.replace(`/submission/flow/continue${location.search}`);
          return;
        }
        setRestoring(false);
        return;
      }
      if (restored.state && restored.state.verified) {
        history.replace(`/submission/flow/step/2${location.search}`);
        return;
      }
    }
    setRestoreFailed(false);
    setRestoring(false);
    // Keep the header countdown live; gate redirects if it closed.
    refreshGate({ history, search: location.search, verify: true });
  };

  useEffect(() => {
    trackSubmissionEvent("sf_email_code_viewed", {});
    // A stale error from an earlier step must not surface under this step's copy.
    clearError();
    boot();
    // Step 2's film center picker (a new account's next page): its options are on their way
    // while the code is typed and the account is made (cityOptions.ts).
    preloadCityOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goNext = () => {
    autoContinue.stop();
    history.replace(`/submission/flow/step/2${location.search}`);
  };

  // The pending signup lives in the server's memory for 90 minutes; once it is
  // gone (window over, or the server restarted) this page has nothing to
  // verify. The continue page offers log in or a fresh start, as on boot.
  const leaveIfSessionGone = () => {
    if (getVerifyErrorCode() !== "sessionNotFound") return false;
    history.replace(`/submission/flow/continue${location.search}`);
    return true;
  };

  const onComplete = async (value: string) => {
    trackSubmissionEvent("sf_email_code_submitted", {});
    submittedAt.current = Date.now();
    const slowTimer = setTimeout(() => showSlow(true), CHECKLIST_AFTER_MS);
    const result = await verifyEmailCode({ code: value });
    clearTimeout(slowTimer);
    if (!result) {
      // A late failure goes back to the boxes, with its message under them.
      showSlow(false);
      // Read the reason from the model's fresh state, not the errorCode prop
      // captured in this closure before the dispatch (that reports the
      // PREVIOUS attempt, so the first failure always looked like `null`).
      // A wrong or dead code empties the boxes; a transient failure keeps it for Enter.
      if (shouldClearCode(getVerifyErrorCode())) setCode("");
      trackSubmissionEvent("sf_email_code_failed", {
        reason: getVerifyErrorCode(),
      });
      leaveIfSessionGone();
      return;
    }
    if (result) {
      trackSubmissionEvent("sf_account_created", {
        clientId: result.clientId,
        cityId: result.accountDefaults && result.accountDefaults.cityId,
        geoSource: result.accountDefaults && result.accountDefaults.geoSource,
      });
      if (slowRef.current) {
        // Every step ticked, for a moment, before the verified screen.
        await wait(CHECKLIST_HOLD_MS);
        showSlow(false);
      }
      // Moves on by itself after a short count shown on Continue, which
      // still works for anyone who'd rather not wait.
      autoContinue.start();
    }
  };

  const onResend = async () => {
    setCode("");
    await resendEmailCode();
    trackSubmissionEvent("sf_email_code_resend", {});
    leaveIfSessionGone();
  };

  if (slow) {
    return (
      <SubmissionLayout step={1}>
        <AccountSetupProgress
          done={verifyStatus === "verified"}
          since={submittedAt.current}
        />
      </SubmissionLayout>
    );
  }

  if (verifyStatus === "verified") {
    return (
      <SubmissionLayout step={1}>
        <SuccessWrap>
          <SuccessIcon src={SuccessCheckIcon} alt="" aria-hidden="true" />
          <SuccessTitle role="status">{i18next.t("Code verified!")}</SuccessTitle>
          <SuccessText>
            {i18next.t("Please continue with the next steps to get your account ready.")}
          </SuccessText>
          <PrimaryButton onClick={goNext}>{autoContinueLabel(autoContinue.secondsLeft)}</PrimaryButton>
          {accountDefaults && isMockApi() && (
            <DefaultsNote>
              {`account defaults → city: ${accountDefaults.cityName || "none"} · self-represented: ${String(
                accountDefaults.selfRepresented
              )} · job alerts: ${String(accountDefaults.sendBreakdowns)}`}
            </DefaultsNote>
          )}
        </SuccessWrap>
      </SubmissionLayout>
    );
  }

  if (restoreFailed) {
    return (
      <SubmissionLayout step={1}>
        <CenterWrap>
          <Title>{i18next.t("We couldn't load your verification")}</Title>
          <SubTitle>{errorMessage || i18next.t("Please try again in a moment.")}</SubTitle>
          <PrimaryButton
            onClick={() => {
              setRestoring(true);
              boot();
            }}
          >
            {i18next.t("Try again")}
          </PrimaryButton>
        </CenterWrap>
      </SubmissionLayout>
    );
  }

  const locked = verifyStatus === "locked";
  const verifying = verifyStatus === "verifying";

  return (
    <SubmissionLayout step={1}>
      <CenterWrap>
        <Title>{i18next.t("Check your email")}</Title>
        <SubTitle>
          {i18next.t("Enter the 6 digit code we sent to")}
          <br />
          <Email>{maskedEmail || i18next.t("your email")}</Email>
        </SubTitle>

        {restoring ? (
          <Restoring aria-busy="true">{i18next.t("One moment…")}</Restoring>
        ) : (
          <>
            <SubmissionCodeInput
              length={CODE_LENGTH}
              value={code}
              onChange={setCode}
              onComplete={onComplete}
              isInvalid={verifyStatus === "error" || locked}
              isDisabled={verifying || locked}
              isChecking={verifying}
            />

            {errorCode && (
              <ErrorText role="alert">
                {errorCode === "invalidCode"
                  ? attemptsRemaining === 1
                    ? i18next.t("Invalid code. 1 attempt left")
                    : i18next.t("Invalid code. {{count}} attempts left", {
                        count: attemptsRemaining,
                      })
                  : errorCode === "codeExpired"
                  ? i18next.t("That code has expired. Tap Resend to get a new one.")
                  : errorCode === "codeReplaced"
                  ? i18next.t("This code was replaced. Check your latest email.")
                  : errorCode === "tooManyAttempts"
                  ? i18next.t("Too many attempts. Please request a new code.")
                  : errorCode === "emailAlreadyRegistered"
                  ? i18next.t("This email already has an account")
                  : errorCode === "authTokenFailed"
                  ? i18next.t("Account created — please log in to continue")
                  : errorCode === "network"
                  ? errorMessage
                  : i18next.t("We couldn't check your code — try again")}
              </ErrorText>
            )}

            <ResendCountdown
              availableAt={resendAvailableAt}
              onResend={onResend}
              disabled={verifying}
            />

            <SpamHint>{i18next.t("Don't forget to check spam folder!")}</SpamHint>
          </>
        )}

        {isMockApi() && <MockHint>mock code: 123456 · wrong codes decrement attempts</MockHint>}
      </CenterWrap>
    </SubmissionLayout>
  );
};

const CenterWrap = styled.div`
  text-align: center;
  margin-top: 32px;
`;

const SuccessWrap = styled.div`
  text-align: center;
  margin-top: 80px;
`;

// The membership app's check, as on "Phone verified!" (PhoneVerificationPage).
const SuccessIcon = styled.img`
  height: 60px;
  margin-bottom: 24px;
`;

const SuccessTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 8px;
`;

const SuccessText = styled.p`
  font-size: 14px;
  color: #53627c;
  margin: 0 0 28px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 10px;
`;

const SubTitle = styled.p`
  font-size: 15px;
  color: #53627c;
  margin: 0 0 24px;
  line-height: 22px;
`;

const Email = styled.strong`
  color: #122640;
  overflow-wrap: break-word;
`;

const Restoring = styled.div`
  font-size: 14px;
  color: #53627c;
  padding: 30px 0;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin-top: 4px;
`;

const SpamHint = styled.div`
  font-size: 13px;
  color: #53627c;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const DefaultsNote = styled.div`
  margin-top: 20px;
  font-size: 11px;
  color: #8a94a6;
  font-family: monospace;
`;

const MockHint = styled.div`
  margin-top: 24px;
  font-size: 11px;
  color: #8a94a6;
  font-family: monospace;
`;

interface StateProps {
  sessionId: string | null;
  maskedEmail: string;
  attemptsRemaining: number;
  verifyStatus: string;
  resendAvailableAt: number;
  errorCode: string | null;
  errorMessage: string | null;
  accountDefaults: iRootState["submissionSignupModel"]["accountDefaults"];
}

interface DispatchProps {
  verifyEmailCode: (payload: { code: string }) => Promise<any>;
  resendEmailCode: () => Promise<any>;
  loadSignupState: () => Promise<{
    ok: boolean;
    reason?: "missing" | "gone" | "transient";
    state?: any;
  }>;
  refreshGate: (payload: { history: any; search?: string; verify?: boolean }) => Promise<any>;
  clearError: () => void;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  sessionId: state.submissionSignupModel.sessionId,
  maskedEmail: state.submissionSignupModel.maskedEmail,
  attemptsRemaining: state.submissionSignupModel.attemptsRemaining,
  verifyStatus: state.submissionSignupModel.verifyStatus,
  resendAvailableAt: state.submissionSignupModel.resendAvailableAt,
  errorCode: state.submissionSignupModel.errorCode,
  errorMessage: state.submissionSignupModel.errorMessage,
  accountDefaults: state.submissionSignupModel.accountDefaults,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  verifyEmailCode: dispatch.submissionSignupModel.verifyEmailCode,
  resendEmailCode: dispatch.submissionSignupModel.resendEmailCode,
  loadSignupState: dispatch.submissionSignupModel.loadSignupState,
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  clearError: dispatch.submissionSignupModel.clearError,
});

export default connect(mapStateToProps, mapDispatchToProps)(SignupVerifyEmailPage);
